/**
 * Global Footer rendered inside every Layout route.
 *
 * Brand-first monochrome block (white on deep ink) with three grouped
 * link columns + a compliance note. Fully responsive: 1-col on mobile,
 * 3-col on md+.
 */
import * as React from "react";
import { Link } from "react-router-dom";

const GROUPS = [
  {
    heading: "Marketplace",
    links: [
      { to: "/", label: "Browse parts" },
      { to: "/forums", label: "Forums" },
      { to: "/sell", label: "Sell a part" },
      { to: "/search", label: "Search" },
    ],
  },
  {
    heading: "Company",
    links: [
      { to: "/about", label: "About us" },
      { to: "/contact", label: "Contact" },
      { to: "/faq", label: "FAQ" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { to: "/terms", label: "Terms of service" },
      { to: "/privacy", label: "Privacy policy" },
    ],
  },
];

export function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="mt-16 border-t-2 border-surface-border bg-ink text-white"
    >
      <div className="mx-auto max-w-6xl px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-[1.2fr_repeat(3,minmax(0,1fr))] gap-10">
          <div>
            <div className="inline-flex items-center gap-3 mb-3">
              <img
                src="/logo.png"
                alt=""
                aria-hidden
                className="h-10 w-10 object-contain"
              />
              <span className="font-display text-2xl font-black tracking-tight">
                Parts <span className="text-brand">Buoy</span>
              </span>
            </div>
            <p className="text-white/70 text-sm leading-relaxed max-w-xs">
              The niche marketplace and community for boat parts. Buy real OEM
              gear, sell what you've got, and talk shop with people who
              actually work on motors.
            </p>
          </div>

          {GROUPS.map((group) => (
            <nav key={group.heading} aria-label={group.heading}>
              <h3 className="text-xs uppercase tracking-[0.18em] text-white/60 font-bold mb-3">
                {group.heading}
              </h3>
              <ul className="flex flex-col gap-2">
                {group.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      data-testid={`footer-link-${link.to.replace("/", "") || "home"}`}
                      className="text-white/80 hover:text-brand font-semibold text-sm transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/50">
          <p>© {year} Parts Buoy, LLC. All rights reserved.</p>
          <p>
            Stripe payments. US shipping via seller. Pricing in USD.
          </p>
        </div>
      </div>
    </footer>
  );
}
